import { z } from 'zod';

/**
 * `GET /v1/notifications` — the signed-in user's own in-app inbox, as the
 * backend's `InboxService.list` shapes it (`{ items, nextCursor }`, newest
 * first, optional `category` filter).
 *
 * Why the panel reads a USER inbox. Thirteen events in
 * `admin-alerts.subscriber.ts` — a trade held for manual review, a dispute
 * opened, a seller who did not ship in time (ko-backend#1115), a prize past
 * its delivery deadline, a custody shortfall… — are dispatched to every
 * active ADMIN as EMAIL + IN_APP. The in-app half was written «as the durable,
 * queryable record an admin inbox would read from the day one exists»; the
 * panel had none, so for a year the only channel that reached a person was
 * the email. These rows are that record, filtered to `MODERATION`, which is
 * the category every one of those alerts carries (ko-admin#344).
 */
export const InboxItem = z
  .object({
    id: z.string(),
    /** Loose: a category or a type added server-side must list, not blank the tray. */
    category: z.string(),
    type: z.string(),
    /** Rendered by the backend's templates, in Spanish. `null` on a bare dispatch. */
    title: z.string().nullable(),
    body: z.string().nullable(),
    /** What the alert is about — `orderId`, `tradeId`, `disputeId`… — so a row can open its screen. */
    payloadJson: z.record(z.string(), z.unknown()).nullable(),
    readAt: z.string().nullable(),
    createdAt: z.string(),
  })
  .passthrough();
export type InboxItem = z.infer<typeof InboxItem>;

export const InboxPage = z
  .object({
    items: z.array(InboxItem),
    nextCursor: z.string().nullable(),
  })
  .passthrough();
export type InboxPage = z.infer<typeof InboxPage>;

/** `POST /v1/notifications/:id/read` — idempotent; a second read returns the same. */
export const InboxReadResult = z.object({ ok: z.literal(true) }).passthrough();
export type InboxReadResult = z.infer<typeof InboxReadResult>;
